import { useState, useEffect, useMemo, useRef } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2, Link2, Copy } from 'lucide-react';
import { Button } from '../Common';
import { csvImportAPI } from '../../api/endpoints';
import type { CSVValidateResponse, CSVValidatedRow } from '../../types';

// Column groups for display
const VENUE_FIELDS = [
  { key: 'venue_name', label: 'Venue Name' },
  { key: 'venue_url', label: 'URL' },
  { key: 'venue_unparsed_address', label: 'Address' },
  { key: 'contact_period', label: 'Contact Period' },
  { key: 'concert_period', label: 'Concert Period' },
  { key: 'comments', label: 'Comments' },
];

const MAX_CONTACTS = 3;

/** The review table's row filter. Everything but `all` narrows the rows on display. */
type RowFilter = 'all' | 'match_name_city' | 'match_name' | 'contact_existing' | 'invalid' | 'unselected';

interface ValidationStepProps {
  sessionId: string;
  confirmedMapping: Record<string, string | null>;
  fixedValues: Record<string, string>;
  onValidationComplete: (response: CSVValidateResponse) => void;
  validateResponse: CSVValidateResponse | null;
  selectedRows: Set<number>;
  onSelectedRowsChange: (rows: Set<number>) => void;
  onImport: () => void;
  onBack: () => void;
}

export function ValidationStep({
  sessionId,
  confirmedMapping,
  fixedValues,
  onValidationComplete,
  validateResponse,
  selectedRows,
  onSelectedRowsChange,
  onImport,
  onBack,
}: ValidationStepProps) {
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<RowFilter>('all');
  // `unselected` is read from a snapshot taken when the filter is picked: ticking a row to re-add
  // it must not make it vanish from under the cursor.
  const [unselectedSnapshot, setUnselectedSnapshot] = useState<Set<number> | null>(null);
  const hasStartedValidation = useRef(false);

  // Send field -> column_index mapping directly to the backend
  // (supports multiple fields mapped to the same column)
  const backendMapping = confirmedMapping;

  // Run validation on mount
  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasStartedValidation.current) return;
    if (!validateResponse && sessionId) {
      hasStartedValidation.current = true;
      validateRows();
    }
  }, [sessionId]);

  const validateRows = async () => {
    setIsValidating(true);
    setError(null);
    setFilter('all');
    setUnselectedSnapshot(null);

    try {
      // Filter out empty fixed values
      const cleanFixedValues = Object.fromEntries(
        Object.entries(fixedValues).filter(([, v]) => v.trim())
      );
      const response = await csvImportAPI.validate({
        session_id: sessionId,
        confirmed_mapping: backendMapping,
        ...(Object.keys(cleanFixedValues).length > 0 && { fixed_values: cleanFixedValues }),
      });
      onValidationComplete(response.data);

      // Initialize selected rows with all valid rows
      const validRowNumbers = new Set(
        response.data.rows
          .filter((row) => row.is_valid)
          .map((row) => row.row_number)
      );
      onSelectedRowsChange(validRowNumbers);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to validate CSV data');
    } finally {
      setIsValidating(false);
    }
  };

  // Calculate import counts based on selected rows
  const importCounts = useMemo(() => {
    if (!validateResponse) return { venues: 0, contacts: 0 };

    let venues = 0;
    let contacts = 0;

    for (const row of validateResponse.rows) {
      if (selectedRows.has(row.row_number)) {
        venues++;
        contacts += row.contacts_count ?? (row.has_contact ? 1 : 0);
      }
    }

    return { venues, contacts };
  }, [validateResponse, selectedRows]);

  const handleToggleRow = (rowNumber: number, isValid: boolean) => {
    if (!isValid) return; // Can't select invalid rows

    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowNumber)) {
      newSelected.delete(rowNumber);
    } else {
      newSelected.add(rowNumber);
    }
    onSelectedRowsChange(newSelected);
  };

  // Rows behind each filter. All but `unselected` are the server's own counts — it decided the
  // verdicts, so re-deriving them here would be a second implementation of the same rule.
  // `unselected` is selection state, which only the client knows, and covers valid rows only: an
  // invalid row was never selectable and has its own filter.
  const filterCounts = useMemo(() => {
    const summary = validateResponse?.summary;
    let unselected = 0;
    for (const row of validateResponse?.rows ?? []) {
      if (row.is_valid && !selectedRows.has(row.row_number)) unselected++;
    }
    return {
      nameCity: summary?.venues_match_name_city ?? 0,
      nameOnly: summary?.venues_match_name ?? 0,
      contactExisting: summary?.rows_with_existing_contacts ?? 0,
      invalid: summary?.invalid ?? 0,
      unselected,
    };
  }, [validateResponse, selectedRows]);

  // How many rows of each duplicate tier are still ticked. Nothing left ticked means the tier's
  // button turns back into "Reselect", so one button covers both directions.
  const selectedPerTier = useMemo(() => {
    const counts = { name_city: 0, name: 0 };
    for (const row of validateResponse?.rows ?? []) {
      if (row.venue_match && selectedRows.has(row.row_number)) counts[row.venue_match]++;
    }
    return counts;
  }, [validateResponse, selectedRows]);

  const visibleRows = useMemo(() => {
    const rows = validateResponse?.rows ?? [];
    switch (filter) {
      case 'match_name_city':
        return rows.filter((row) => row.venue_match === 'name_city');
      case 'match_name':
        return rows.filter((row) => row.venue_match === 'name');
      case 'contact_existing':
        return rows.filter((row) => row.contacts_existing?.some(Boolean));
      case 'invalid':
        return rows.filter((row) => !row.is_valid);
      case 'unselected':
        return rows.filter((row) => unselectedSnapshot?.has(row.row_number));
      default:
        return rows;
    }
  }, [validateResponse, filter, unselectedSnapshot]);

  const applyFilter = (next: RowFilter) => {
    setUnselectedSnapshot(
      next === 'unselected'
        ? new Set(
            (validateResponse?.rows ?? [])
              .filter((row) => row.is_valid && !selectedRows.has(row.row_number))
              .map((row) => row.row_number)
          )
        : null
    );
    setFilter(next);
  };

  // Bulk selection acts on what is on display, so a filter doubles as the way to put a whole
  // group back: narrow to it, then "Select Valid".
  const handleSelectVisibleValid = () => {
    const next = new Set(selectedRows);
    for (const row of visibleRows) {
      if (row.is_valid) next.add(row.row_number);
    }
    onSelectedRowsChange(next);
  };

  const handleDeselectVisible = () => {
    const next = new Set(selectedRows);
    for (const row of visibleRows) next.delete(row.row_number);
    onSelectedRowsChange(next);
  };

  /** Flip a whole duplicate tier on or off, wherever those rows are and whatever the filter shows.
   *  Anything still ticked in the tier means the click drops it; an empty tier puts it all back. */
  const handleToggleMatches = (tier: 'name_city' | 'name') => {
    if (!validateResponse) return;
    const dropping = selectedPerTier[tier] > 0;
    const next = new Set(selectedRows);
    for (const row of validateResponse.rows) {
      if (row.venue_match !== tier) continue;
      if (dropping) next.delete(row.row_number);
      else if (row.is_valid) next.add(row.row_number);
    }
    onSelectedRowsChange(next);
  };

  // Get mapped venue fields (only those that have data - from column mapping or fixed values)
  const mappedVenueFields = useMemo(() => {
    return VENUE_FIELDS.filter((f) => confirmedMapping[f.key] || fixedValues[f.key]);
  }, [confirmedMapping, fixedValues]);

  // Detect which contact indices (1..MAX_CONTACTS) have any mapped fields
  const mappedContactIndices = useMemo(() => {
    const indices: number[] = [];
    for (let n = 1; n <= MAX_CONTACTS; n++) {
      const hasMapping = ['name', 'email', 'phone', 'role', 'flag'].some(
        (s) => confirmedMapping[`contact_${n}_${s}`] || fixedValues[`contact_${n}_${s}`]
      );
      if (hasMapping) indices.push(n);
    }
    return indices;
  }, [confirmedMapping, fixedValues]);

  if (isValidating) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-dark-accent animate-spin mb-4" />
        <p className="text-dark-text-secondary">Validating CSV data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
          <Button onClick={validateRows}>Retry</Button>
        </div>
      </div>
    );
  }

  if (!validateResponse) {
    return null;
  }

  const { summary } = validateResponse;
  const isFiltered = filter !== 'all';

  const filterChips: { key: RowFilter; label: string; count: number; title: string }[] = [
    { key: 'all', label: 'All rows', count: validateResponse.rows.length, title: 'Every row in the file' },
    {
      key: 'match_name_city',
      label: 'Same name & city',
      count: filterCounts.nameCity,
      title: 'Venues you already have under this exact name and city — almost certainly the same place',
    },
    {
      key: 'match_name',
      label: 'Same name only',
      count: filterCounts.nameOnly,
      title: 'You have this name already, but in another city — it may or may not be the same place',
    },
    {
      key: 'contact_existing',
      label: 'Contact you have',
      count: filterCounts.contactExisting,
      title: 'Rows with an email address you already have a contact for — that contact is linked to the new venue, never duplicated',
    },
    { key: 'invalid', label: 'Cannot import', count: filterCounts.invalid, title: 'Rows with an error that stops them being imported' },
    {
      key: 'unselected',
      label: 'Not selected',
      count: filterCounts.unselected,
      title: 'Valid rows currently left out of the import',
    },
  ];

  // Determine row background color
  const getRowClassName = (row: CSVValidatedRow) => {
    if (!row.is_valid) {
      return 'bg-red-500/10'; // Invalid venue - red
    }
    if (!selectedRows.has(row.row_number)) {
      return 'bg-yellow-500/10'; // Valid but unselected - yellow
    }
    return 'bg-green-500/5 hover:bg-green-500/10'; // Valid and selected - green
  };

  // "Already on file" marker: the tier, with the venue it collided with in the hover text
  const renderMatchBadge = (row: CSVValidatedRow) => {
    if (!row.venue_match) return null;
    const strong = row.venue_match === 'name_city';
    const where = row.venue_match_city
      ? `${row.venue_match_name} — ${row.venue_match_city}`
      : `${row.venue_match_name} (no city recorded)`;
    return (
      <span
        title={
          strong
            ? `You already have "${where}". Same name and city, so almost certainly the same place — importing this row would give you a second copy of it.`
            : `You already have "${where}". Same name but a different city, so it may well be a different place — worth a look before you decide.`
        }
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs whitespace-nowrap border ${
          strong
            ? 'bg-orange-500/15 border-orange-500/40 text-orange-300'
            : 'bg-amber-500/10 border-amber-500/30 text-amber-200/80'
        }`}
      >
        <Copy className="w-3 h-3 shrink-0" />
        {strong ? 'name & city' : 'name only'}
      </span>
    );
  };

  // Per-contact-slot cell class: red only if that specific slot is invalid
  const getContactSlotClassName = (row: CSVValidatedRow, n: number, isFirst: boolean) => {
    const base = isFirst ? 'border-l-2 border-blue-400/30' : '';
    // contacts_valid is slot-indexed: index 0 = contact 1
    const slotValid = row.contacts_valid?.[n - 1] ?? true;
    if (row.is_valid && !slotValid) {
      return `${base} bg-red-500/20`;
    }
    return base;
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-green-400 font-medium">{summary.valid} valid</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
          <XCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400 font-medium">{summary.invalid} invalid</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-dark-elevated rounded-lg">
          <span className="text-dark-text-secondary">
            {summary.with_contacts} with contacts, {summary.venues_only} venues only
          </span>
        </div>
        {!!(filterCounts.nameCity || filterCounts.nameOnly) && (
          <div
            className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-lg text-orange-300"
            title="You already have a venue of this name. Importing never merges — every row you leave ticked becomes a new venue, so deselect the ones you already have."
          >
            <Copy className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">
              {filterCounts.nameCity + filterCounts.nameOnly} you may already have
              <span className="font-normal opacity-80">
                {' '}({filterCounts.nameCity} same name &amp; city, {filterCounts.nameOnly} same
                name only)
              </span>
            </span>
          </div>
        )}
        {!!summary.contacts_existing && (
          <div
            className="flex items-center gap-2 px-4 py-2 bg-dark-elevated rounded-lg"
            title="An email address can only belong to one contact, so the contact you already have is linked to the new venue rather than created a second time. Nothing to deselect here — this is what you want."
          >
            <span className="text-dark-text-secondary">
              {summary.contacts_existing} contact{summary.contacts_existing !== 1 ? 's' : ''} you
              already have — linked, not duplicated
            </span>
          </div>
        )}
      </div>

      {/* Selection controls — the bulk ones act on the rows currently on display */}
      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" size="sm" onClick={handleSelectVisibleValid}>
          {isFiltered ? 'Select all shown' : 'Select all valid'}
        </Button>
        <Button variant="secondary" size="sm" onClick={handleDeselectVisible}>
          {isFiltered ? 'Deselect all shown' : 'Deselect all'}
        </Button>
        {filterCounts.nameCity > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleToggleMatches('name_city')}
            title={
              selectedPerTier.name_city > 0
                ? 'Leave out every row whose name and city you already have'
                : 'Put those rows back into the import'
            }
          >
            {selectedPerTier.name_city > 0 ? 'Deselect' : 'Reselect'} {filterCounts.nameCity} (same
            name &amp; city)
          </Button>
        )}
        {filterCounts.nameOnly > 0 && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleToggleMatches('name')}
            title={
              selectedPerTier.name > 0
                ? 'Leave out every row whose name you already have, but in another city'
                : 'Put those rows back into the import'
            }
          >
            {selectedPerTier.name > 0 ? 'Deselect' : 'Reselect'} {filterCounts.nameOnly} (same name
            only)
          </Button>
        )}
      </div>

      {/* Row filter */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-dark-text-muted mr-1">Show</span>
        {filterChips.map(({ key, label, count, title }) => {
          if (key !== 'all' && !count) return null;
          const active = filter === key;
          return (
            <button
              key={key}
              type="button"
              title={title}
              onClick={() => applyFilter(key)}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                active
                  ? 'bg-dark-accent/20 border-dark-accent/50 text-dark-accent'
                  : 'bg-dark-elevated border-dark-border text-dark-text-secondary hover:text-dark-text-primary'
              }`}
            >
              {label}
              {key !== 'all' && <span className="ml-1 opacity-70">({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Table with grouped columns */}
      <div className="overflow-x-auto max-h-96 overflow-y-auto border border-dark-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-dark-surface">
            {/* Group header row */}
            <tr className="border-b border-dark-border">
              <th className="py-1 px-3" colSpan={5}></th>
              {mappedVenueFields.length > 0 && (
                <th
                  colSpan={mappedVenueFields.length}
                  className="py-1 px-3 text-center text-xs uppercase tracking-wider text-dark-accent font-semibold bg-dark-accent/5 border-l-2 border-dark-accent/30"
                >
                  Venue Info
                </th>
              )}
              {mappedContactIndices.length > 0 && (
                <th
                  colSpan={mappedContactIndices.length}
                  className="py-1 px-3 text-center text-xs uppercase tracking-wider text-blue-400 font-semibold bg-blue-400/5 border-l-2 border-blue-400/30"
                >
                  Contact Info
                </th>
              )}
            </tr>
            {/* Column header row */}
            <tr className="border-b border-dark-border">
              <th className="text-left py-2 px-3 text-dark-text-secondary font-medium w-12">
                Import
              </th>
              <th className="text-left py-2 px-3 text-dark-text-secondary font-medium w-16">
                Status
              </th>
              <th
                className="text-left py-2 px-3 text-dark-text-secondary font-medium whitespace-nowrap"
                title="Whether a venue of this name is already in your list"
              >
                You Have It?
              </th>
              <th className="text-left py-2 px-3 text-dark-text-secondary font-medium">
                Errors
              </th>
              <th className="text-left py-2 px-3 text-dark-text-secondary font-medium w-12">
                #
              </th>
              {/* Venue columns */}
              {mappedVenueFields.map((field, idx) => (
                <th
                  key={field.key}
                  className={`text-left py-2 px-3 text-dark-text-secondary font-medium whitespace-nowrap ${
                    idx === 0 ? 'border-l-2 border-dark-accent/30' : ''
                  }`}
                >
                  {field.label}
                </th>
              ))}
              {/* One column per mapped contact slot */}
              {mappedContactIndices.map((n, idx) => (
                <th
                  key={n}
                  className={`text-left py-2 px-3 text-dark-text-secondary font-medium whitespace-nowrap ${idx === 0 ? 'border-l-2 border-blue-400/30' : ''}`}
                >
                  {mappedContactIndices.length > 1 ? `Contact ${n}` : 'Contact'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row: CSVValidatedRow) => (
              <tr
                key={row.row_number}
                className={`border-b border-dark-border/50 ${getRowClassName(row)}`}
              >
                <td className="py-2 px-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.row_number)}
                    onChange={() => handleToggleRow(row.row_number, row.is_valid)}
                    disabled={!row.is_valid}
                    className="w-4 h-4 rounded-sm border-dark-border bg-dark-elevated text-dark-accent focus:ring-dark-accent disabled:opacity-50"
                  />
                </td>
                <td className="py-2 px-3">
                  {row.is_valid ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                </td>
                <td className="py-2 px-3">{renderMatchBadge(row)}</td>
                <td className="py-2 px-3 text-red-400 text-xs">
                  {[...row.errors, ...row.contact_errors].join(', ')}
                </td>
                <td className="py-2 px-3 text-dark-text-secondary">
                  {row.row_number}
                </td>
                {/* Venue data */}
                {mappedVenueFields.map((field, idx) => (
                  <td
                    key={field.key}
                    className={`py-2 px-3 text-dark-text-primary whitespace-nowrap max-w-xs truncate ${
                      idx === 0 ? 'border-l-2 border-dark-accent/30' : ''
                    }`}
                  >
                    {row.data[field.key] || '-'}
                  </td>
                ))}
                {/* One cell per mapped contact slot, independently colored */}
                {mappedContactIndices.map((n, idx) => {
                  const name  = row.data[`contact_${n}_name`];
                  const email = row.data[`contact_${n}_email`];
                  const phone = row.data[`contact_${n}_phone`];
                  const role  = row.data[`contact_${n}_role`];
                  const hasData = !!(name || email || phone || role);
                  const isExisting = row.contacts_existing?.[n - 1] ?? false;
                  const parts: string[] = [];
                  if (name)  parts.push(name);
                  if (email) parts.push(`<${email}>`);
                  else if (phone) parts.push(phone);
                  if (role && !email) parts.push(`(${role})`);
                  const text = parts.join(' ');
                  return (
                    <td
                      key={n}
                      className={`py-2 px-3 text-xs text-dark-text-primary whitespace-nowrap max-w-[180px] truncate ${getContactSlotClassName(row, n, idx === 0)}`}
                      title={
                        isExisting
                          ? `${text} — you already have this contact: it will be linked to this venue, not created a second time.`
                          : text
                      }
                    >
                      {isExisting && <Link2 className="inline w-3 h-3 mr-1 text-cyan-400 shrink-0" />}
                      {hasData ? (text || '-') : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-dark-text-muted">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-green-500/20 border border-green-500/30"></div>
          <span>Selected for import</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-yellow-500/20 border border-yellow-500/30"></div>
          <span>Valid, but left out of the import</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-red-500/20 border border-red-500/30"></div>
          <span>Cannot import — this row has an error</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-red-500/40 border border-red-500/50"></div>
          <span>Cannot import this contact — the venue comes in without it</span>
        </div>
        <div className="flex items-center gap-2">
          <Copy className="w-3.5 h-3.5 text-orange-300" />
          <span>You may already have this venue — hover the badge to see which one</span>
        </div>
        <div className="flex items-center gap-2">
          <Link2 className="w-3.5 h-3.5 text-cyan-400" />
          <span>You already have this contact — it gets linked to the new venue, not duplicated</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center">
        <Button variant="secondary" onClick={onBack}>
          Back to Mapping
        </Button>

        <div className="flex items-center gap-4">
          <span className="text-dark-text-secondary">
            {selectedRows.size} of {validateResponse.rows.length} rows selected
            {isFiltered && (
              <span className="text-dark-text-muted">
                {' '}· {visibleRows.length} shown
              </span>
            )}
          </span>
          <Button
            onClick={onImport}
            disabled={selectedRows.size === 0}
          >
            Import {importCounts.venues} venue{importCounts.venues !== 1 ? 's' : ''}
            {importCounts.contacts > 0 && `, ${importCounts.contacts} contact${importCounts.contacts !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
